import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Sparkles, TrendingUp, TrendingDown, Wallet, Users, Download, X } from "lucide-react";
import { toast } from "sonner";
import { DatePresetPills } from "@/components/DatePresetPills";

/**
 * Admin · Loyalty audit
 *
 * Snapshot of the program economics + a scrollable ledger of every earn and
 * redeem event. Answers the two questions that matter for a discount program:
 *   1. How much real cash did we take in (net of redemptions)?
 *   2. What's our current outstanding liability (points we owe out)?
 *
 * Filters (email / date range / kind) narrow the ledger, KPIs, and CSV export
 * to the same slice so investigating one buyer's history takes seconds.
 */
export default function AdminLoyalty() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);
  // Filter state — email is debounced so typing doesn't hammer the endpoint;
  // dates + kind refetch immediately.
  const [email, setEmail] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [kind, setKind] = useState("");

  const params = useMemo(() => {
    const p = {};
    if (email.trim()) p.email = email.trim();
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (kind) p.kind = kind;
    return p;
  }, [email, dateFrom, dateTo, kind]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/admin/loyalty/summary", { params })
        .then((r) => { setData(r.data); setErr(""); })
        .catch((e) => setErr(e.response?.data?.detail || e.message));
    }, 250);
    return () => clearTimeout(t);
  }, [params]);

  const clearFilters = () => { setEmail(""); setDateFrom(""); setDateTo(""); setKind(""); };
  const anyFilterActive = !!(email || dateFrom || dateTo || kind);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      // Pass the same filter params so the CSV matches what's on screen.
      const res = await api.get("/admin/loyalty/ledger.csv", { params, responseType: "blob" });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const filename = match ? match[1] : `nebula-loyalty-ledger-${Date.now()}.csv`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not download the ledger.");
    } finally {
      setDownloading(false);
    }
  };

  if (err) return <div className="p-8 text-[#A30414]" data-testid="admin-loyalty-error">Failed to load: {err}</div>;
  if (!data) return <div className="p-8 text-sm font-mono uppercase tracking-wider text-[#9AA3AF]">Loading loyalty audit…</div>;

  const { config, points, money, recent_ledger, top_redeemers } = data;
  const redemptionRate = money.orders_count > 0
    ? Math.round((money.orders_with_redeem / money.orders_count) * 100)
    : 0;

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="admin-loyalty-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow text-[#3FFFE0]"><Sparkles className="inline h-3.5 w-3.5 mr-1" /> Loyalty audit</div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold mt-1">Points program economics.</h1>
          <p className="text-sm text-[#9AA3AF] mt-2 max-w-2xl">
            Earn rate <span className="font-mono text-white">{config.earn_rate_per_dollar} pts / $1</span> ·
            Redeem rate <span className="font-mono text-white">{config.points_per_dollar_redeem} pts = $1</span> ·
            Expiry <span className="font-mono text-white">{config.expiry_months} months</span>.
          </p>
        </div>
        <button
          onClick={downloadCsv}
          disabled={downloading}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[#3FFFE0]/50 bg-[#3FFFE0]/10 text-[#3FFFE0] hover:bg-[#3FFFE0]/20 active:scale-95 transition text-sm font-medium disabled:opacity-50 disabled:cursor-wait self-start"
          data-testid="loyalty-csv-download"
          title="Full ledger as CSV — every earn and redeem event, oldest first."
        >
          <Download className="h-4 w-4" />
          {downloading ? "Preparing CSV…" : "Download ledger CSV"}
        </button>
      </div>

      {/* Filter row — narrows KPIs, ledger, and CSV to the same slice. */}
      <div
        className="border border-[#1F2937] rounded-md bg-[#0E141B] p-4 flex flex-col md:flex-row md:items-end gap-3"
        data-testid="loyalty-filter-row"
      >
        <div className="flex-1 min-w-[220px]">
          <label className="block font-mono text-[10.5px] uppercase tracking-[0.16em] text-[#9AA3AF] mb-1.5">Buyer email</label>
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Substring, case-insensitive"
            className="w-full px-3 py-2 rounded-md border border-[#1F2937] bg-[#0B1017] text-sm text-white placeholder-[#4F5560] outline-none focus:border-[#3FFFE0]"
            data-testid="loyalty-filter-email"
          />
        </div>
        <div>
          <label className="block font-mono text-[10.5px] uppercase tracking-[0.16em] text-[#9AA3AF] mb-1.5">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 rounded-md border border-[#1F2937] bg-[#0B1017] text-sm text-white outline-none focus:border-[#3FFFE0]"
            data-testid="loyalty-filter-from"
          />
        </div>
        <div>
          <label className="block font-mono text-[10.5px] uppercase tracking-[0.16em] text-[#9AA3AF] mb-1.5">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 rounded-md border border-[#1F2937] bg-[#0B1017] text-sm text-white outline-none focus:border-[#3FFFE0]"
            data-testid="loyalty-filter-to"
          />
        </div>
        <div>
          <label className="block font-mono text-[10.5px] uppercase tracking-[0.16em] text-[#9AA3AF] mb-1.5">Type</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="px-3 py-2 rounded-md border border-[#1F2937] bg-[#0B1017] text-sm text-white outline-none focus:border-[#3FFFE0]"
            data-testid="loyalty-filter-kind"
          >
            <option value="">All</option>
            <option value="earn">Earn</option>
            <option value="redeem">Redeem</option>
          </select>
        </div>
        {anyFilterActive && (
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-[#1F2937] text-[#9AA3AF] hover:text-white hover:border-[#3FFFE0] transition text-[13px]"
            data-testid="loyalty-filter-clear"
          >
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>

      {/* Quick-pick pills — one-click accounting slices. */}
      <DatePresetPills
        from={dateFrom}
        to={dateTo}
        onChange={(f, t) => { setDateFrom(f); setDateTo(t); }}
        includeLast30
        className="-mt-4"
      />

      {data.filtered && (
        <div
          className="text-[12px] font-mono uppercase tracking-[0.14em] text-[#3FFFE0]"
          data-testid="loyalty-filtered-notice"
        >
          Showing filtered slice — KPIs, ledger{data.filters.email ? "" : ", top redeemers"}, and CSV all reflect the current filter.
        </div>
      )}

      {/* Top-line KPIs — the numbers admins actually care about at a glance. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI
          icon={<Wallet className="h-5 w-5 text-[#3FFFE0]" />}
          label="Net Revenue"
          value={`$${money.net_revenue_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          sub={`${money.orders_count} settled orders`}
          testid="kpi-revenue"
        />
        <KPI
          icon={<TrendingDown className="h-5 w-5 text-[#FF4FD8]" />}
          label="Loyalty Paid Out"
          value={`$${money.loyalty_paid_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          sub={`${points.redeemed.toLocaleString()} pts redeemed · ${redemptionRate}% of orders`}
          testid="kpi-loyalty-paid"
        />
        <KPI
          icon={<TrendingUp className="h-5 w-5 text-[#3FFFE0]" />}
          label="Points Issued"
          value={points.issued.toLocaleString()}
          sub={`≈ $${(points.issued / config.points_per_dollar_redeem).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lifetime`}
          testid="kpi-points-issued"
        />
        <KPI
          icon={<Sparkles className="h-5 w-5 text-[#FFB454]" />}
          label="Outstanding Liability"
          value={`$${money.outstanding_liability_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          sub={`${points.outstanding.toLocaleString()} pts still redeemable`}
          testid="kpi-liability"
        />
      </div>

      {/* Top redeemers — spot power-users of the program (or fraud patterns).
          Hidden when the email filter narrows to a single buyer since a
          leaderboard of one is redundant with the ledger below. */}
      {!data.filters.email && (
      <section>
        <h2 className="text-lg font-display font-semibold text-white flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-[#3FFFE0]" /> Top redeemers
        </h2>
        <div className="border border-[#1F2937] rounded-md overflow-hidden bg-[#0E141B]">
          {top_redeemers.length === 0 ? (
            <div className="p-4 text-sm text-[#9AA3AF]">No redemptions yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-loyalty-top">
              <thead className="bg-[#0B1017] text-[10.5px] uppercase tracking-[0.16em] text-[#9AA3AF] font-mono">
                <tr>
                  <th className="text-left px-4 py-2.5">Researcher</th>
                  <th className="text-right px-4 py-2.5">Points redeemed</th>
                  <th className="text-right px-4 py-2.5">USD saved</th>
                  <th className="text-right px-4 py-2.5">Redemptions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1F2937]">
                {top_redeemers.map((u) => (
                  <tr key={u.user_id} className="hover:bg-[#131A22]">
                    <td className="px-4 py-2.5">
                      <div className="text-white">{u.name || "—"}</div>
                      <div className="font-mono text-[11px] text-[#9AA3AF]">{u.email || u.user_id}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-white">{u.points.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#3FFFE0]">${u.usd_saved.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-white">{u.redemptions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
      )}

      {/* Full ledger — every single earn and redeem event. Newest first. */}
      <section>
        <h2 className="text-lg font-display font-semibold text-white flex items-center gap-2 mb-3">
          Ledger <span className="font-mono text-[11px] uppercase tracking-wider text-[#9AA3AF]">(latest {recent_ledger.length})</span>
        </h2>
        <div className="border border-[#1F2937] rounded-md overflow-hidden bg-[#0E141B]">
          <table className="w-full text-sm" data-testid="admin-loyalty-ledger">
            <thead className="bg-[#0B1017] text-[10.5px] uppercase tracking-[0.16em] text-[#9AA3AF] font-mono">
              <tr>
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Kind</th>
                <th className="text-left px-4 py-2.5">Researcher</th>
                <th className="text-left px-4 py-2.5">Order</th>
                <th className="text-right px-4 py-2.5">Points</th>
                <th className="text-right px-4 py-2.5">USD value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1F2937]">
              {recent_ledger.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-[#9AA3AF]">Ledger is empty.</td></tr>
              ) : (
                recent_ledger.map((e) => {
                  const isRedeem = e.kind === "redeem";
                  return (
                    <tr key={e.id} className="hover:bg-[#131A22]" data-testid={`ledger-row-${e.id}`}>
                      <td className="px-4 py-2.5 font-mono text-[11.5px] text-[#9AA3AF] whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-wider ${isRedeem ? "bg-[#3B0714] text-[#FF6B8A]" : "bg-[#0B3B2F] text-[#3FFFE0]"}`}>
                          {e.kind}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="text-white">{e.user_name || "—"}</div>
                        <div className="font-mono text-[11px] text-[#9AA3AF]">{e.user_email || "?"}</div>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[11.5px] text-white">{e.order_number || "—"}</td>
                      <td className={`px-4 py-2.5 text-right font-mono ${isRedeem ? "text-[#FF6B8A]" : "text-[#3FFFE0]"}`}>
                        {isRedeem ? "−" : "+"}{Math.abs(Number(e.points || 0)).toLocaleString()}
                      </td>
                      <td className={`px-4 py-2.5 text-right font-mono ${isRedeem ? "text-[#FF6B8A]" : "text-[#9AA3AF]"}`}>
                        {isRedeem ? "−" : ""}${Number(e.usd_value || 0).toFixed(2)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function KPI({ icon, label, value, sub, testid }) {
  return (
    <div className="border border-[#1F2937] rounded-md p-4 bg-[#0E141B]" data-testid={testid}>
      <div className="flex items-center gap-2">
        {icon}
        <div className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-[#9AA3AF]">{label}</div>
      </div>
      <div className="mt-2 font-display text-2xl md:text-3xl font-semibold text-white">{value}</div>
      {sub && <div className="mt-1 text-[11.5px] text-[#9AA3AF]">{sub}</div>}
    </div>
  );
}
